import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Navbar from './Navbar';
import Footer from './Footer';
import {
  FaClock, FaCog, FaTruck, FaCheckCircle, FaTimesCircle,
  FaBoxOpen, FaReceipt, FaUser, FaEnvelope, FaPhone,
  FaMapMarkerAlt, FaTimes, FaArrowLeft, FaStar,
  FaUndo, FaExclamationTriangle, FaWeight, FaFlask,
  FaCreditCard
} from 'react-icons/fa';

const BASE = 'https://evercorenutrition.com';

const statusConfig = {
  'Pending': { color: '#f59e0b', bg: '#fef3c7', icon: <FaClock /> },
  'Processing': { color: '#3b82f6', bg: '#dbeafe', icon: <FaCog /> },
  'Completed': { color: '#10b981', bg: '#d1fae5', icon: <FaCheckCircle /> },
  'Delivered': { color: '#10b981', bg: '#d1fae5', icon: <FaCheckCircle /> },
  'Cancelled': { color: '#ef4444', bg: '#fee2e2', icon: <FaTimesCircle /> },
  'Return Requested': { color: '#ef4444', bg: '#fee2e2', icon: <FaUndo /> },
  'Cancel Requested': { color: '#ef4444', bg: '#fee2e2', icon: <FaExclamationTriangle /> },
  'Order Placed': { color: '#3b82f6', bg: '#dbeafe', icon: <FaClock /> },
};

const cancelReasons = [
  { value: 'ordered_by_mistake', label: 'Ordered by mistake' },
  { value: 'found_better_price', label: 'Found a better price' },
  { value: 'changed_mind', label: 'Changed my mind' },
  { value: 'duplicate_order', label: 'Duplicate order' },
  { value: 'other', label: 'Other reason' },
];

const returnReasons = [
  { value: 'damaged', label: 'Product Damaged' },
  { value: 'defective', label: 'Product Defective' },
  { value: 'wrong_item', label: 'Wrong Item Delivered' },
  { value: 'expired', label: 'Product Expired' },
  { value: 'quality_issue', label: 'Quality Issue' },
  { value: 'other', label: 'Other Reason' },
];

const getImage = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BASE}${url}`;
};

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Cancel modal states
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelComments, setCancelComments] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState(false);

  // Return modal states
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnComments, setReturnComments] = useState('');
  const [returnEmail, setReturnEmail] = useState('');
  const [returnMedia, setReturnMedia] = useState([]);
  const [submittingReturn, setSubmittingReturn] = useState(false);

  // Review states
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [submittedProducts, setSubmittedProducts] = useState([]);
  const [hasReadReturnPolicy, setHasReadReturnPolicy] = useState(false);
  const [image_urls, setImageUrls] = useState([]);

  useEffect(() => {
    return () => {
      returnMedia.forEach(file => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });
    };
  }, [returnMedia]);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please login to view order details');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${BASE}/ev_admin/api/customer/orders/${orderId}/`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) throw new Error('Session expired. Please login again.');
        throw new Error('Failed to fetch order');
      }

      const data = await response.json();
      // console.log('Order data:', data);

      if (data.success && data.order) {
        setOrder(data.order);
        const reviewed = JSON.parse(localStorage.getItem(`reviewed_products_${orderId}`) || '[]');
        setSubmittedProducts(reviewed);
      } else {
        setError('Order not found');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Could not load order details');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total quantity from all items
  const calculateTotalQuantity = () => {
    if (!order?.items) return 0;
    return order.items.reduce((total, item) => total + (item.quantity || 0), 0);
  };

  const handleCancelOrder = () => {
    setCancelReason('');
    setCancelComments('');
    setCancelReasonError(false);
    setCancelModalOpen(true);
  };

  const submitCancelOrder = async () => {
    if (!cancelReason) {
      setCancelReasonError(true);
      return;
    }

    setCancelModalOpen(false);
    setCancelling(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${BASE}/ev_admin/api/orders/${order.order_id}/cancel/`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason: cancelReason, comments: cancelComments.trim() })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setOrder(prev => ({ ...prev, status_display: 'Cancelled' }));
        Swal.fire({ title: 'Order Cancelled', text: 'Your order has been cancelled successfully.', icon: 'success', timer: 2000, showConfirmButton: false });
      } else {
        throw new Error(data.message || 'Cancellation failed');
      }
    } catch (err) {
      Swal.fire({ title: 'Error', text: err.message || 'Could not cancel order', icon: 'error', timer: 2000, showConfirmButton: false });
    } finally {
      setCancelling(false);
    }
  };

  const handleReturnRequest = async (e) => {
    e.preventDefault();

    if (!hasReadReturnPolicy) {
      Swal.fire({ title: 'Policy Agreement Required', text: 'Please read and agree to the return policy before submitting.', icon: 'warning', timer: 2000, showConfirmButton: false });
      return;
    }
    if (!returnReason) {
      Swal.fire({ title: 'Reason Required', text: 'Please select a reason for return.', icon: 'warning', timer: 2000, showConfirmButton: false });
      return;
    }
    if (!returnComments.trim()) {
      Swal.fire({ title: 'Comments Required', text: 'Please provide additional comments.', icon: 'warning', timer: 2000, showConfirmButton: false });
      return;
    }

    setSubmittingReturn(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Please login to submit return request');

      let customerId = null;
      try {
        const userData = localStorage.getItem('userData');
        if (userData) {
          const parsed = JSON.parse(userData);
          customerId = parsed.id || parsed.customer_id;
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }

      const formData = new FormData();
      formData.append('customer_id', customerId || '');
      formData.append('order_id', order.order_id);
      formData.append('return_reason', returnReason);
      formData.append('pickup_address', order.pickup_address || '');
      formData.append('comments', returnComments.trim());
      formData.append('email', returnEmail.trim());

      if (order.items && order.items.length > 0) {
        const allItems = order.items.map(item => ({
          order_item_id: item.id,
          quantity: item.quantity,
          product_name: item.item_type === 'combo' ? (item.combo_name || item.name) : (item.product_data?.name || 'Product')
        }));
        formData.append('items', JSON.stringify(allItems));
      }

      const imageFiles = returnMedia.filter(file => file.type.startsWith('image/'));
      imageFiles.forEach((file, index) => {
        if (index < 5) formData.append(`evidence_image_${index + 1}`, file);
      });

      const response = await fetch(`${BASE}/ev_admin/api/return/request/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      // FIRST: Close the modal and clean up
      returnMedia.forEach(file => { 
        if (file.preview) URL.revokeObjectURL(file.preview); 
      });
      
      // Reset all form states
      setReturnReason('');
      setReturnComments('');
      setReturnMedia([]);
      setHasReadReturnPolicy(false);
      setReturnEmail('');
      
      // Close the modal FIRST
      setReturnModalOpen(false);
      
      // Wait a tiny bit for modal to close, then show popup
      setTimeout(async () => {
        if (response.ok && data.success) {
          // Show success popup
          await Swal.fire({ 
            title: 'Success!', 
            text: data.message || 'Return request submitted successfully.', 
            icon: 'success', 
            confirmButtonText: 'OK',
            allowOutsideClick: false
          });
          // Refresh order data after popup closes
          fetchOrder();
        } else {
          // Show error popup
          await Swal.fire({ 
            title: 'Error', 
            text: data.message || data.error || 'Failed to submit return request', 
            icon: 'error', 
            confirmButtonText: 'OK' 
          });
        }
      }, 100);
      
    } catch (err) {
      console.error('Return submission error:', err);
      // Close modal if still open
      setReturnModalOpen(false);
      // Show error popup after modal closes
      setTimeout(async () => {
        await Swal.fire({ 
          title: 'Error', 
          text: err.message || 'Could not submit return request. Please try again.', 
          icon: 'error', 
          confirmButtonText: 'OK' 
        });
      }, 100);
    } finally {
      setSubmittingReturn(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: '16px', color: '#666' }}>Loading order details...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <Navbar />
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <FaTimesCircle size={48} color="#ef4444" style={{ marginBottom: '12px' }} />
          <p style={{ color: '#ef4444', fontSize: '16px', marginBottom: '20px' }}>{error || 'Order not found'}</p>
          <button className="btn btn-dark" onClick={() => navigate('/UserAccount')}>Back to Account</button>
        </div>
        <Footer />
      </>
    );
  }

  const status = statusConfig[order.status_display] || statusConfig['Pending'];
  const isCompleted = order.status_display === 'Completed' || order.status_display === 'Delivered';
  const isCancellable = ['Pending', 'Processing', 'Order Placed'].includes(order.status_display);
  const isReturnable = isCompleted && !order.status_display?.includes('Return') && !order.status_display?.includes('Cancel');
  const isCancelledOrReturned = order.status_display?.includes('Cancel') || order.status_display?.includes('Return');
  const totalQuantity = calculateTotalQuantity();

  return (
    <>
      <Navbar />

      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain">
                <h2>Order Details</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                    <li className="breadcrumb-item"><a href="/UserAccount">My Account</a></li>
                    <li className="breadcrumb-item active">Order Details</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-b-space">
        <div className="container-fluid-lg">
          <div className="row g-4">

            {/* Left Column */}
            <div className="col-lg-8">

              {/* Order Header */}
              <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <h4 style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Order #{order.order_id}</h4>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                      Placed on {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span style={{ background: status.bg, color: status.color, padding: '6px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      {status.icon} {order.status_display}
                    </span>
                    {order.payment_status === 'completed' && !isCancelledOrReturned && (
                      <span style={{ background: '#d1fae5', color: '#10b981', padding: '6px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: '600' }}>
                        <FaCheckCircle size={12} style={{ marginRight: '4px' }} /> Paid
                      </span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
                  {isCancellable && (
                    <button
                      onClick={handleCancelOrder}
                      disabled={cancelling}
                      style={{ padding: '8px 20px', background: '#fff', border: '2px solid #ef4444', color: '#ef4444', borderRadius: '8px', cursor: cancelling ? 'not-allowed' : 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      {cancelling ? <span className="spinner-border spinner-border-sm" /> : <FaTimesCircle />}
                      Cancel Order
                    </button>
                  )}
                  {isReturnable && (
                    <button
                      onClick={() => setReturnModalOpen(true)}
                      style={{ padding: '8px 20px', background: '#fff', border: '2px solid #10b981', color: '#10b981', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <FaUndo /> Request Return
                    </button>
                  )}
                </div>
              </div>

              {/* Items Section */}
              <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <h5 style={{ marginBottom: '20px', fontWeight: '600' }}>
                  Items Ordered ({order.items?.length || 0})
                </h5>

                {/* Items List */}
                {(order.items || []).map((item, index) => {
                  const isComboItem = item.item_type === 'combo';
                  const displayName = isComboItem 
                    ? (item.combo_name || item.name || 'Combo Product')
                    : (item.product_data?.name || item.name || 'Product');
                  
                  const productImage = getImage(item.combo_image || item.product_image || item.product_data?.image || item.product_data?.main_image);
                  
                  const itemTotal = (parseFloat(item.unit_price || item.price) * item.quantity).toFixed(2);
                  
                  const variantInfo = !isComboItem && item.product_data?.variant;

                  return (
                    <div
                      key={item.id || index}
                      style={{ display: 'flex', gap: '16px', padding: '16px 0', borderBottom: index < (order.items.length - 1) ? '1px solid #f0f0f0' : 'none' }}
                    >
                      <div style={{ width: '80px', height: '80px', flexShrink: 0, position: 'relative' }}>
                        {productImage ? (
                          <img
                            src={productImage}
                            alt={displayName}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px', padding: '4px' }}
                          />
                        ) : (
                          <div style={{ width: '100%', height: '100%', background: '#f5f5f5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaBoxOpen size={24} color="#ccc" />
                          </div>
                        )}
                        {isComboItem && (
                          <div style={{ position: 'absolute', top: '4px', right: '4px', background: '#0d6efd', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>
                            COMBO
                          </div>
                        )}
                      </div>

                      <div style={{ flex: 1 }}>
                        <h6 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>
                          {displayName}
                          {isComboItem && (
                            <span style={{ marginLeft: '8px', background: '#e0e7ff', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', color: '#0d6efd' }}>
                              Combo Item
                            </span>
                          )}
                        </h6>

                        {variantInfo && (
                          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                            {variantInfo.flavour && variantInfo.flavour !== 'Default' && (
                              <span style={{ fontSize: '12px', color: '#666', background: '#f5f5f5', padding: '2px 8px', borderRadius: '4px' }}>
                                <FaFlask size={10} style={{ marginRight: '4px' }} />
                                {variantInfo.flavour}
                              </span>
                            )}
                            {variantInfo.weight_value && (
                              <span style={{ fontSize: '12px', color: '#666', background: '#f5f5f5', padding: '2px 8px', borderRadius: '4px' }}>
                                <FaWeight size={10} style={{ marginRight: '4px' }} />
                                {variantInfo.weight_value}{variantInfo.weight_unit}
                              </span>
                            )}
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                          <span style={{ fontSize: '13px', color: '#666' }}>Qty: {item.quantity}</span>
                          <span style={{ fontSize: '13px', color: '#666' }}>₹{parseFloat(item.unit_price || item.price).toFixed(2)} each</span>
                        </div>
                      </div>

                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <p style={{ margin: 0, fontWeight: '700', fontSize: '16px' }}>₹{itemTotal}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="col-lg-4">
              <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', position: 'sticky', top: '20px' }}>
                <h5 style={{ marginBottom: '20px', fontWeight: '600' }}>Order Summary</h5>

                <div style={{ marginBottom: '16px' }}>

                    {/* TOTAL QUANTITY */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', padding: '8px 0', borderTop: '1px dashed #e5e7eb', borderBottom: '1px dashed #e5e7eb' }}>
                    <span style={{ color: '#666', fontWeight: '500' }}>
                      <FaBoxOpen size={12} style={{ marginRight: '6px' }} />
                      Total Quantity
                    </span>
                    <span style={{ fontWeight: '600', color: '#0d6efd' }}>
                      {totalQuantity} {totalQuantity === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ color: '#666' }}>Subtotal</span>
                    <span>₹{parseFloat(order.subtotal || 0).toFixed(2)}</span>
                  </div>
                  
                
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ color: '#666' }}>Shipping</span>
                    <span>{parseFloat(order.shipping_charge) > 0 ? `₹${parseFloat(order.shipping_charge).toFixed(2)}` : 'FREE'}</span>
                  </div>
                  {parseFloat(order.discount_amount) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', color: '#10b981' }}>
                      <span>Discount</span>
                      <span>-₹{parseFloat(order.discount_amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '12px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '18px' }}>
                    <span>Total</span>
                    <span style={{ color: '#0d6efd' }}>₹{parseFloat(order.total_amount).toFixed(2)}</span>
                  </div>
                </div>

                <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: '#666' }}>Payment Method</span>
                    <span style={{ fontWeight: '500' }}>{order.payment_type_display}</span>
                  </div>
                </div>

                {order.customer_details && (
                  <div style={{ marginTop: '20px' }}>
                    <h6 style={{ marginBottom: '12px', fontWeight: '600' }}>Customer Details</h6>
                    <p style={{ margin: '0 0 4px', fontSize: '14px' }}>
                      <strong>{order.customer_details.full_name || `${order.customer_details.first_name} ${order.customer_details.last_name}`}</strong>
                    </p>
                    <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#666' }}>
                      <FaEnvelope size={12} style={{ marginRight: '6px' }} /> {order.customer_details.email}
                    </p>
                    <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>
                      <FaPhone size={12} style={{ marginRight: '6px' }} /> {order.customer_details.phone}
                    </p>
                  </div>
                )}

{order.pickup_address && (() => {

  let address = order.pickup_address;

  // ✅ Convert JSON string to object
  if (typeof address === "string") {
    try {
      address = JSON.parse(address);
    } catch (e) {
      address = null;
    }
  }

  return (
    <div style={{ marginTop: '20px' }}>

      <h6 style={{ marginBottom: '12px', fontWeight: '600' }}>
        <FaMapMarkerAlt size={12} style={{ marginRight: '6px' }} />
        Delivery Address
      </h6>

      {address && typeof address === "object" ? (
        <div
          style={{
            fontSize: '13px',
            color: '#666',
            lineHeight: '1.7'
          }}
        >
          <div style={{ fontWeight: '600', color: '#222' }}>
            {address.receiver_name}
          </div>

          <div style={{ marginBottom: '6px' }}>
            📞 {address.receiver_mobile}
          </div>

          <div>
            {address.street}
          </div>

          <div>
            {address.city}, {address.state} - {address.zip_code}
          </div>

          <div>
            {address.country}
          </div>
        </div>
      ) : (
        <p
          style={{
            margin: 0,
            fontSize: '13px',
            color: '#666',
            lineHeight: '1.5'
          }}
        >
          {order.pickup_address}
        </p>
      )}

    </div>
  );
})()}

                <button
                  onClick={() => navigate('/UserAccount')}
                  style={{ width: '100%', marginTop: '24px', padding: '12px', background: '#000', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                >
                  Back to My Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cancel Order Modal */}
      {cancelModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '480px', width: '100%', position: 'relative' }}>
            <button onClick={() => setCancelModalOpen(false)} style={{ position: 'absolute', top: '14px', right: '14px', background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>
              <FaTimes />
            </button>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '700' }}>Cancel Order</h3>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>Please tell us why you're cancelling</p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '20px' }}>
              {cancelReasons.map(r => (
                <div
                  key={r.value}
                  onClick={() => { setCancelReason(r.value); setCancelReasonError(false); }}
                  style={{ border: `1.5px solid ${cancelReason === r.value ? '#ef4444' : '#e5e7eb'}`, borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', background: cancelReason === r.value ? '#fff5f5' : '#fff' }}
                >
                  <span style={{ fontSize: '13px' }}>{r.label}</span>
                </div>
              ))}
            </div>
            {cancelReasonError && <p style={{ fontSize: '12px', color: '#ef4444', marginBottom: '12px' }}>Please select a reason</p>}

            <textarea
              value={cancelComments}
              onChange={e => setCancelComments(e.target.value)}
              placeholder="Additional comments (optional)"
              rows="3"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', marginBottom: '20px' }}
            />

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setCancelModalOpen(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Keep Order</button>
              <button onClick={submitCancelOrder} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#ef4444', color: 'white', cursor: 'pointer' }}>Cancel Order</button>
            </div>
          </div>
        </div>
      )}

      {/* Return Request Modal */}
      {returnModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px', overflow: 'auto' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '550px', width: '100%', maxHeight: '90vh', overflow: 'auto', position: 'relative' }}>
            <button
              onClick={() => {
                returnMedia.forEach(file => { if (file.preview) URL.revokeObjectURL(file.preview); });
                setReturnModalOpen(false);
                setReturnMedia([]);
                setReturnReason('');
                setReturnComments('');
                setHasReadReturnPolicy(false);
              }}
              style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#666', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
            >
              <FaTimes />
            </button>

            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '700', paddingRight: '30px' }}>Request Return</h3>

            <form onSubmit={handleReturnRequest}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Reason for Return *</label>
                <select value={returnReason} onChange={e => setReturnReason(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} required>
                  <option value="">Select a reason</option>
                  {returnReasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Additional Comments *</label>
                <textarea value={returnComments} onChange={e => setReturnComments(e.target.value)} rows="4" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} required />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Your Email for Return Updates *</label>
                <input type="email" placeholder="Enter your email address" value={returnEmail} onChange={e => setReturnEmail(e.target.value)} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }} />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Upload Evidence *</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    const images = files.filter(f => f.type.startsWith('image/'));
                    const currentImages = returnMedia.filter(f => f.type.startsWith('image/'));
                    const remainingSlots = 5 - currentImages.length;
                    const newFiles = images.slice(0, Math.min(images.length, remainingSlots));
                    if (newFiles.length > 0) setReturnMedia(prev => [...prev, ...newFiles]);
                    if (images.length > remainingSlots) {
                      Swal.fire({ title: 'Maximum Images Reached', text: `You can only upload up to 5 images total.`, icon: 'warning', timer: 2000, showConfirmButton: false });
                    }
                    e.target.value = '';
                  }}
                  style={{ width: '100%', padding: '8px', marginBottom: '12px' }}
                />
                <small style={{ fontSize: '11px', color: '#666', display: 'block' }}>
                  Upload up to 5 images (JPG, PNG) — {returnMedia.filter(f => f.type.startsWith('image/')).length}/5 used
                </small>
              </div>

              {returnMedia.length > 0 && (
                <div style={{ marginBottom: '20px', padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <p style={{ fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}>Selected Images ({returnMedia.length}/5):</p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {returnMedia.map((file, idx) => {
                      const previewUrl = URL.createObjectURL(file);
                      return (
                        <div key={idx} style={{ position: 'relative', width: '80px', height: '80px' }}>
                          <img src={previewUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }} />
                          <button
                            type="button"
                            onClick={() => {
                              const newMedia = [...returnMedia];
                              URL.revokeObjectURL(previewUrl);
                              newMedia.splice(idx, 1);
                              setReturnMedia(newMedia);
                            }}
                            style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >×</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '20px', padding: '16px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '20px' }}>📋</span>
                  <strong style={{ color: '#92400e' }}>Return Policy</strong>
                </div>
                <p style={{ fontSize: '13px', color: '#78350f', marginBottom: '12px' }}>
                  Please read our return policy before submitting. Contact us using the details below for any queries.
                </p>
                <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span>📞</span>
                    <span style={{ fontSize: '13px' }}><strong>Phone:</strong> +91 89699 77190</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span>✉️</span>
                    <span style={{ fontSize: '13px' }}><strong>Email:</strong> admin@evercorenutrition.com</span>
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <a href="/policies/return" target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#10b981', textDecoration: 'underline' }}>
                    Read Full Return Policy →
                  </a>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={hasReadReturnPolicy} onChange={e => setHasReadReturnPolicy(e.target.checked)} style={{ marginTop: '2px', cursor: 'pointer' }} />
                  <span style={{ fontSize: '13px', color: '#374151' }}>I have read and agree to the Return Policy.</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    returnMedia.forEach(file => { if (file.preview) URL.revokeObjectURL(file.preview); });
                    setReturnModalOpen(false);
                    setReturnMedia([]);
                    setReturnReason('');
                    setReturnComments('');
                    setHasReadReturnPolicy(false);
                  }}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingReturn || !hasReadReturnPolicy}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: !hasReadReturnPolicy ? '#9ca3af' : '#10b981', color: 'white', cursor: (!hasReadReturnPolicy || submittingReturn) ? 'not-allowed' : 'pointer' }}
                >
                  {submittingReturn ? 'Submitting...' : 'Submit Return Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default OrderDetail;